import {
  Layers,
  Upload,
  ArrowRight,
  ArrowDownLeft,
  ArrowUpRight as Outflow,
  Wallet,
  Aperture,
} from 'lucide-react';
import { SterlingScore } from '@/components/SterlingScore';
import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHead } from '@/layout/AppShell';
import { useAuth } from '@/lib/auth';
import { useInsightAction, useInsights, useSummary } from '@/lib/queries';
import { compactMoney, greeting, maskText, money, percent, signedMoney } from '@/lib/format';
import { gaugeDash, linePaths, sparklinePoints } from '@/lib/charts';
import {
  Bar,
  Button,
  Card,
  CardSkeleton,
  CardTitle,
  Dot,
  Empty,
  ErrorBox,
  Eyebrow,
  Segmented,
  Tag,
} from '@/components/ui';
import { ArrowUpRight } from '@/components/icons';
import { useOutletContext } from 'react-router-dom';
import type { ShellContext } from '@/lib/shell';
import s from './Overview.module.css';

type Range = '1M' | '3M' | '12M';

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openRecord, openBulkRecord, toast } = useOutletContext<ShellContext>();
  const [range, setRange] = useState<Range>('12M');
  const { data, isLoading, error } = useSummary(range);
  const { data: insights } = useInsights();
  const insightAction = useInsightAction();
  const gradientId = useId();

  const currency = user?.currency ?? 'USD';

  const trend = useMemo(() => {
    if (!data) return { line: '', area: '' };
    return linePaths(
      data.netWorthTrend.map((p) => p.valueCents),
      720,
      168,
    );
  }, [data]);

  const incomeSpark = useMemo(
    () => (data ? sparklinePoints(data.series.map((m) => m.incomeCents)) : ''),
    [data],
  );
  const spendSpark = useMemo(
    () => (data ? sparklinePoints(data.series.map((m) => m.expenseCents)) : ''),
    [data],
  );

  const rangeSwitch = (
    <Segmented<Range>
      ariaLabel="Time range"
      mono
      value={range}
      onChange={setRange}
      options={[
        { value: '1M', label: '1M' },
        { value: '3M', label: '3M' },
        { value: '12M', label: '12M' },
      ]}
    />
  );

  if (error) {
    return (
      <>
        <PageHead title="Overview" />
        <ErrorBox>Couldn’t load your summary. {(error as Error).message}</ErrorBox>
      </>
    );
  }

  if (isLoading || !data) {
    return (
      <>
        <PageHead title={user ? greeting(user.name) : 'Overview'} action={rangeSwitch} />
        <div className={s.stack}>
          <div className={s.heroGrid}>
            <div className={s.hero}>
              <CardSkeleton lines={3} height={360} />
            </div>
            <CardSkeleton lines={2} height={120} />
            <CardSkeleton lines={2} height={120} />
            <CardSkeleton lines={2} height={120} />
            <CardSkeleton lines={2} height={120} />
          </div>
        </div>
      </>
    );
  }

  const { kpis } = data;
  const hasData = data.series.some((m) => m.incomeCents > 0 || m.expenseCents > 0);

  // Month-over-month, to match the "this month" figure sitting beside it.
  // Comparing against the start of the range instead would read as a wild
  // percentage on a 12-month view and contradict the text next to it.
  const points = data.netWorthTrend;
  const previous = points[points.length - 2]?.valueCents ?? 0;
  const latest = points[points.length - 1]?.valueCents ?? 0;
  const changePct = previous !== 0 ? ((latest - previous) / Math.abs(previous)) * 100 : 0;

  const openInsights = insights?.filter((i) => i.status !== 'DISMISSED') ?? [];

  return (
    <>
      <PageHead
        title={user ? greeting(user.name) : 'Overview'}
        subtitle={
          hasData
            ? `${data.recent.length ? `Last activity ${data.recent[0]!.date}` : 'No activity yet'} · ${range === '12M' ? '12 months' : range === '3M' ? '3 months' : 'this month'} shown`
            : 'Add a transaction or import a bank statement to get started'
        }
        action={rangeSwitch}
      />

      <div className={s.quickActions}>
        <span>Your money at a glance</span>
        <button onClick={openBulkRecord}>
          <Layers size={16} aria-hidden="true" /> Bulk record
        </button>
        <button onClick={() => navigate('/app/transactions?import=1')}>
          <Upload size={16} aria-hidden="true" /> Import statement
        </button>
        <button onClick={() => navigate('/app/budgets')}>
          Plan this month <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
      {!hasData ? (
        <>
          <SterlingScore />
          <Card>
            <Empty
              action={
                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <Button variant="primary" size="sm" onClick={openRecord}>
                    Record a transaction
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/app/transactions?import=1')}
                  >
                    Import a statement
                  </Button>
                </div>
              }
            >
              Nothing to show yet. Record a transaction by hand, or import a bank statement from
              your bank and Sterling will categorize it for you.
            </Empty>
          </Card>
        </>
      ) : (
        <div className={s.stack}>
          {/* ── Hero + KPIs ───────────────────────────────────────────── */}
          <div className={s.heroGrid}>
            <Card className={s.hero}>
              <div className={s.heroCaption}>
                <span>
                  <Aperture size={17} aria-hidden="true" /> THE BIG PICTURE
                </span>
                <span>
                  {range === '12M'
                    ? 'Past 12 months'
                    : range === '3M'
                      ? 'Past 3 months'
                      : 'This month'}
                </span>
              </div>
              <div className={s.heroTop}>
                <div>
                  <div className={s.heroLabel}>Your total net worth</div>
                  <div className={s.netWorth}>{money(kpis.netWorthCents, currency)}</div>
                  <div className={s.deltaRow}>
                    <span className={`${s.deltaChip} ${changePct < 0 ? s.deltaChipDown : ''}`}>
                      <ArrowUpRight />
                      {changePct >= 0 ? '+' : '−'}
                      {Math.abs(changePct).toFixed(1)}%
                    </span>
                    <span className={s.deltaNote}>
                      {signedMoney(kpis.netCents, currency, false)} this month
                    </span>
                  </div>
                </div>
                <div className={s.breakdown}>
                  <div className={s.breakdownRow}>
                    Cash
                    <span className={s.breakdownValue}>{money(kpis.cashCents, currency)}</span>
                  </div>
                  <div className={s.breakdownRow}>
                    Invested
                    <span className={s.breakdownValue}>{money(kpis.investedCents, currency)}</span>
                  </div>
                  <div className={s.breakdownRow}>
                    Credit
                    <span className={s.breakdownValue}>{money(kpis.creditCents, currency)}</span>
                  </div>
                </div>
              </div>

              <svg
                className={s.heroChart}
                viewBox="0 0 720 168"
                preserveAspectRatio="none"
                role="img"
                aria-label={`Net worth trend, ${money(kpis.netWorthCents, currency)} currently`}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor="var(--hero-line, var(--accent))"
                      stopOpacity="0.34"
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--hero-line, var(--accent))"
                      stopOpacity="0"
                    />
                  </linearGradient>
                </defs>
                {[42, 84, 126].map((y) => (
                  <line
                    key={y}
                    x1="0"
                    x2="720"
                    y1={y}
                    y2={y}
                    stroke="#536451"
                    strokeOpacity="0.1"
                    strokeDasharray="3 6"
                  />
                ))}
                <path className={s.trendArea} d={trend.area} fill={`url(#${gradientId})`} />
                <path
                  d={trend.line}
                  className={s.trendLine}
                  fill="none"
                  stroke="var(--hero-line, var(--accent))"
                  strokeWidth="2.4"
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
              </svg>

              <div className={s.axis}>
                {data.netWorthTrend.map((point) => (
                  <div key={point.key} className={s.axisLabel}>
                    {point.label}
                  </div>
                ))}
              </div>
            </Card>

            <Card className={s.kpi}>
              <div className={s.metricHead}>
                <span className={s.incomeIcon}>
                  <ArrowDownLeft size={19} aria-hidden="true" />
                </span>
                <Eyebrow>income · this month</Eyebrow>
              </div>
              <div className={s.kpiValue}>{money(kpis.incomeCents, currency)}</div>
              <svg
                className={s.spark}
                viewBox="0 0 120 26"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polyline
                  points={incomeSpark}
                  fill="none"
                  stroke="var(--accent-2)"
                  strokeWidth="1.8"
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
              </svg>
            </Card>

            <Card className={s.kpi}>
              <div className={s.metricHead}>
                <span className={s.spendIcon}>
                  <Outflow size={19} aria-hidden="true" />
                </span>
                <Eyebrow>spend · this month</Eyebrow>
              </div>
              <div className={s.kpiValue}>{money(kpis.spendCents, currency)}</div>
              <svg
                className={s.spark}
                viewBox="0 0 120 26"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polyline
                  points={spendSpark}
                  fill="none"
                  stroke="rgba(255,124,102,0.9)"
                  strokeWidth="1.8"
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
              </svg>
            </Card>

            <Card className={s.gaugeCard}>
              <div className={s.gaugeWrap}>
                <svg
                  width="86"
                  height="86"
                  viewBox="0 0 86 86"
                  style={{ display: 'block' }}
                  aria-hidden="true"
                >
                  <circle
                    cx="43"
                    cy="43"
                    r="36"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="8"
                  />
                  <circle
                    cx="43"
                    cy="43"
                    r="36"
                    fill="none"
                    stroke="var(--accent-2)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    pathLength={100}
                    strokeDasharray={gaugeDash(kpis.savingsRate)}
                    transform="rotate(-90 43 43)"
                    style={{
                      filter:
                        'drop-shadow(0 0 7px color-mix(in oklab, var(--accent-2) 45%, transparent))',
                    }}
                  />
                </svg>
                <div className={s.gaugeValue}>{percent(kpis.savingsRate)}</div>
              </div>
              <div>
                <Eyebrow>savings rate</Eyebrow>
                <div className={s.kpiNote}>
                  {kpis.savingsRate >= 0
                    ? `You kept ${money(kpis.netCents, currency)} of ${money(kpis.incomeCents, currency)}.`
                    : 'You spent more than you earned this month.'}
                </div>
              </div>
            </Card>

            <Card className={s.kpi}>
              <div className={s.metricHead}>
                <span className={s.runwayIcon}>
                  <Wallet size={19} aria-hidden="true" />
                </span>
                <Eyebrow>cash runway</Eyebrow>
              </div>
              <div className={s.kpiValue}>
                {kpis.runwayMonths === null ? '—' : kpis.runwayMonths}{' '}
                <span className={s.kpiUnit}>months</span>
              </div>
              <div className={s.kpiNote}>
                {kpis.runwayMonths === null
                  ? 'Needs a month of spending to calculate.'
                  : `${money(kpis.cashCents, currency)} liquid at your average burn.`}
              </div>
            </Card>
          </div>

          <SterlingScore />

          {/* ── Advisor insights ──────────────────────────────────────── */}
          {openInsights.length > 0 ? (
            <>
              <div className={s.insightHead}>
                <Dot />
                <span className={s.insightTitle}>
                  Advisor · {openInsights.length} {openInsights.length === 1 ? 'move' : 'moves'} on
                  the table
                </span>
                <span className={s.insightMeta}>computed from your data</span>
              </div>
              <div className={s.insightGrid}>
                {openInsights.slice(0, 3).map((insight) => {
                  const applied = insight.status === 'APPLIED';
                  return (
                    <Card key={insight.id} accent compact className={s.insightCard}>
                      <div className={s.insightTop}>
                        <Tag tone={insight.tone === 'WARN' ? 'warn' : 'accent'}>{insight.tag}</Tag>
                        <span
                          className={s.insightImpact}
                          style={{
                            color: insight.tone === 'WARN' ? 'var(--danger)' : 'var(--positive)',
                          }}
                        >
                          {maskText(insight.impact)}
                        </span>
                      </div>
                      {/* Server-written copy, so the figures in it never went
                          through `money()` — redact them here instead. */}
                      <div className={s.insightBody}>{maskText(insight.body)}</div>
                      <div className={s.insightActions}>
                        <Button
                          size="sm"
                          variant={applied ? 'done' : 'primary'}
                          disabled={applied || insightAction.isPending}
                          onClick={() => {
                            insightAction.mutate(
                              { id: insight.id, action: 'apply' },
                              {
                                onSuccess: () =>
                                  toast.show(
                                    'Queued. Sterling never moves money — do it at your bank when ready.',
                                  ),
                              },
                            );
                          }}
                        >
                          {applied ? 'Queued ✓' : insight.cta}
                        </Button>
                        {!applied ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              insightAction.mutate({ id: insight.id, action: 'dismiss' })
                            }
                          >
                            Dismiss
                          </Button>
                        ) : null}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : null}

          {/* ── Categories / activity / budgets ────────────────────────── */}
          <div className={s.lowerGrid}>
            <Card>
              <CardTitle sub={`This month · ${money(kpis.spendCents, currency)} total`}>
                Where it went
              </CardTitle>
              {data.categories.length === 0 ? (
                <div style={{ marginTop: 20, fontSize: 12.5, color: 'var(--muted)' }}>
                  No spending recorded this month.
                </div>
              ) : (
                <>
                  <div className={s.catBar}>
                    {data.categories.map((category) => (
                      <div
                        key={category.categoryId ?? 'none'}
                        className={s.catBarSeg}
                        style={{ background: category.color, width: `${category.share}%` }}
                        title={`${category.name} ${percent(category.share)}`}
                      />
                    ))}
                  </div>
                  <div className={s.catList}>
                    {data.categories.slice(0, 7).map((category) => (
                      <div key={category.categoryId ?? 'none'} className={s.catRow}>
                        <span className={s.swatch} style={{ background: category.color }} />
                        <span className={`${s.catName} truncate`}>{category.name}</span>
                        <span className={s.catAmount}>{money(category.amountCents, currency)}</span>
                        <span className={s.catDelta} style={{ color: 'var(--muted-2)' }}>
                          {percent(category.share)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>

            <Card>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CardTitle>Recent activity</CardTitle>
                <Button
                  size="sm"
                  style={{ marginLeft: 'auto', color: 'var(--accent-text)' }}
                  onClick={() => navigate('/app/transactions')}
                >
                  All <ArrowRight size={14} aria-hidden="true" />
                </Button>
              </div>
              <div className={s.activityList}>
                {data.recent.map((tx) => (
                  <button
                    key={tx.id}
                    type="button"
                    className={s.activityRow}
                    onClick={() => navigate(`/app/transactions?focus=${tx.id}`)}
                  >
                    <div
                      className={s.activityIcon}
                      style={{
                        background: `color-mix(in oklab, ${tx.category?.color ?? '#888'} 16%, transparent)`,
                      }}
                    >
                      <span
                        className={s.swatch}
                        style={{ background: tx.category?.color ?? '#888' }}
                      />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className={`${s.activityMerchant} truncate`}>{tx.merchant}</div>
                      <div className={s.activityMeta}>
                        {tx.venture ? `Business · ${tx.venture.name}` : tx.category?.name ?? 'Uncategorized'} · {tx.account.name}
                      </div>
                    </div>
                    <div
                      className={s.activityAmount}
                      style={{
                        color: tx.amountCents > 0 ? 'var(--positive)' : 'rgba(242, 238, 231, 0.92)',
                      }}
                    >
                      {signedMoney(tx.amountCents, currency)}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            <Card>
              <CardTitle
                sub={
                  data.budgets.length
                    ? `${data.budgets.filter((b) => b.isOver).length} over of ${data.budgets.length}`
                    : 'None set yet'
                }
              >
                Budgets
              </CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/app/budgets')}
                style={{ marginTop: 12 }}
              >
                Manage monthly budget <ArrowRight size={14} aria-hidden="true" />
              </Button>
              {data.budgets.length === 0 ? (
                <div
                  style={{
                    marginTop: 18,
                    fontSize: 12.5,
                    color: 'var(--muted)',
                    textWrap: 'pretty',
                  }}
                >
                  Plan your month, split your available money, and track category spending.
                </div>
              ) : (
                <div className={s.budgetList}>
                  {data.budgets.map((budget) => {
                    const color = budget.isOver
                      ? 'var(--danger)'
                      : budget.usedPct > 85
                        ? 'var(--warning)'
                        : 'var(--accent-text)';
                    return (
                      <div key={budget.id}>
                        <div className={s.budgetHead}>
                          <span className={`${s.budgetName} truncate`}>{budget.name}</span>
                          <span className={s.budgetValue} style={{ color }}>
                            {compactMoney(budget.usedCents, currency)} /{' '}
                            {compactMoney(budget.limitCents, currency)}
                          </span>
                        </div>
                        <Bar pct={budget.usedPct} color={color} />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
